import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/delivery.scss";
import "../css/terms.scss";

const termsHighlights = [
  { label: "Applies to", value: "Online store & purchases" },
  { label: "Customer care", value: "Human support, Monday–Friday" },
  { label: "Mandatory rights", value: "Always remain unaffected" },
];

const termsSections = [
  {
    id: "agreement",
    title: "About these terms",
    body: [
      "These terms govern use of the " +
        siteConfig.brandName +
        " website and purchases made through our online store. By using the site or submitting an order, you agree to the version available at that time.",
      "If a local consumer law gives you rights that cannot be limited by contract, those rights take priority. Our privacy policy separately explains how personal information is handled.",
    ],
  },
  {
    id: "access",
    title: "Using the website",
    body: [
      "You may browse the site for personal, lawful shopping and information. You must not interfere with security, introduce harmful code, scrape the store at unreasonable scale, impersonate another person or use the content for an unauthorised commercial purpose.",
      "You are responsible for keeping account access details confidential and for information submitted through your account. Tell us promptly if you believe it has been used without permission.",
    ],
  },
  {
    id: "products",
    title: "Products & availability",
    body: [
      "We make reasonable efforts to present colours, scale, materials and details accurately. Screen settings and the character of hand-led processes can create small variations; these differences are part of the design rather than defects.",
      "Products may be available in limited editions. Adding an item to a bag does not reserve it, and availability is confirmed only when we accept the order.",
    ],
  },
  {
    id: "orders",
    title: "Orders & acceptance",
    body: [
      "After checkout, an automated acknowledgement confirms that we received your request; it does not yet mean the order is accepted. A contract is formed when we send confirmation of acceptance or dispatch the goods.",
      "We may decline or cancel an order before acceptance if an item is unavailable, payment cannot be authorised, shipping restrictions apply, information appears incorrect, or we reasonably suspect fraud or misuse. Any captured amount for a cancelled item will be returned.",
    ],
  },
  {
    id: "price",
    title: "Prices, taxes & payment",
    body: [
      "Prices are shown in the currency selected on the store and may include or exclude local taxes as indicated at checkout. Delivery, import duties and other applicable charges are shown separately when they can be calculated in advance.",
      "We may correct an obvious pricing or product-information error before accepting an order and will give you the choice to continue at the corrected price or cancel. Payment is processed through the methods offered at checkout.",
    ],
  },
  {
    id: "delivery",
    title: "Delivery & responsibility",
    body: [
      "Delivery dates are good-faith estimates, not guarantees. Responsibility for the goods passes as required by applicable consumer law, typically when you or a person you nominate receives them.",
      "You are responsible for providing a complete deliverable address and for any destination charges that checkout states are payable by the recipient.",
    ],
    route: "/shipping",
    routeLabel: "Read our shipping guide",
  },
  {
    id: "returns",
    title: "Returns, refunds & faults",
    body: [
      "Change-of-mind returns must follow the request window and condition requirements in our returns policy. Certain personalised, hygiene-sealed or final-sale products may be excluded where the law permits.",
      "If something arrives damaged, faulty or materially different from its description, contact us promptly so we can assess an appropriate repair, replacement, price adjustment or refund.",
    ],
    route: "/returns",
    routeLabel: "Read our returns policy",
  },
  {
    id: "ownership",
    title: "Creative ownership",
    body: [
      "The site and its photographs, artwork, prints, product names, text, graphics and other original material belong to " +
        siteConfig.brandName +
        " or are used with permission. Buying a product does not transfer intellectual-property rights in its design.",
      "You may save or share ordinary links for personal use. Reproduction, modification, resale of content or use that suggests a partnership requires prior written permission.",
    ],
  },
  {
    id: "liability",
    title: "Service limits",
    body: [
      "We aim to keep the site accurate, secure and available, but it may occasionally be interrupted for maintenance or circumstances outside reasonable control. External links are provided for convenience and do not make us responsible for third-party services.",
      "Nothing in these terms excludes responsibility that cannot legally be excluded. To the extent the law permits, we are not responsible for indirect or unforeseeable loss arising from personal use of the site or products.",
    ],
  },
  {
    id: "changes",
    title: "Changes & contact",
    body: [
      "We may update the site or these terms to reflect product, operational or legal changes. Updates apply from publication and do not retroactively change an order already accepted unless required by law or agreed with you.",
      "If any provision is found unenforceable, the remaining provisions continue to apply. Questions are welcome through customer care.",
    ],
    contact: true,
  },
];

function Terms() {
  return (
    <main className="terms policy-page" id="main-content">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">Legal</p>
        <h1>Terms</h1>
        <p>
          The practical terms for browsing our site and shopping the collection.
          Please read them before placing an order.
        </p>
        <p className="policy-page__updated">
          Last updated <time dateTime="2026-09-30">30 September 2026</time>
        </p>
      </header>

      <dl className="policy-page__summary" aria-label="Terms at a glance">
        {termsHighlights.map((item) => (
          <div key={item.label}>
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>

      <div className="policy-page__layout">
        <aside className="policy-page__aside" aria-label="On this page">
          <p>On this page</p>
          <ol>
            {termsSections.map((section) => (
              <li key={section.id}>
                <a href={"#" + section.id}>{section.title}</a>
              </li>
            ))}
          </ol>
          <div className="policy-page__help">
            <span>Need clarification?</span>
            <Link to="/contact">Contact customer care</Link>
          </div>
        </aside>

        <article className="policy-page__content">
          {termsSections.map((section, index) => (
            <section id={section.id} key={section.id}>
              <span className="policy-page__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <h2>{section.title}</h2>
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {section.route && (
                  <Link className="policy-page__inline-link" to={section.route}>
                    {section.routeLabel}
                  </Link>
                )}
                {section.contact && (
                  <p>
                    Email{" "}
                    <a
                      href={
                        "mailto:" + siteConfig.contact.customerServiceEmail
                      }
                    >
                      {siteConfig.contact.customerServiceEmail}
                    </a>
                    .
                  </p>
                )}
              </div>
            </section>
          ))}
        </article>
      </div>
    </main>
  );
}

export default Terms;
